import React, { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import type { ExtraConfig } from '@gov-nx/core/app/extra-config/context.types';
import { getPagePartSlug } from '@gov-nx/module/page/definitions/PageMap';
import AuthorizedRoute from '../../routes/AuthorizedRoute';
import { AutorizacePage } from './AutorizacePage';

const DashboardPage = lazy(() =>
	import('./DashboardPage').then(({ DashboardPage }) => ({
		default: DashboardPage,
	}))
);

const RegistrZPPage = lazy(() =>
	import('./RegistrZPPage').then(({ RegistrZPPage }) => ({
		default: RegistrZPPage,
	}))
);
const RegistrSVListPage = lazy(() =>
	import('./RegistrSVListPage').then(({ RegistrSVListPage }) => ({
		default: RegistrSVListPage,
	}))
);
const RegistrSVDetailPage = lazy(() =>
	import('./RegistrSVDetailPage').then(({ RegistrSVDetailPage }) => ({
		default: RegistrSVDetailPage,
	}))
);
const PodporaPage = lazy(() =>
	import('./PodporaPage').then(({ PodporaPage }) => ({ default: PodporaPage }))
);
const ZdravDokumentacePage = lazy(() =>
	import('./ZdravDokumentacePage').then(({ ZdravDokumentacePage }) => ({
		default: ZdravDokumentacePage,
	}))
);

const MojeSouboryPage = lazy(() =>
	import('./MojeSouboryPage').then(({ MojeSouboryPage }) => ({
		default: MojeSouboryPage,
	}))
);
const UserManualPage = lazy(() =>
	import('./UserManualPage').then(({ UserManualPage }) => ({
		default: UserManualPage,
	}))
);
const DrozdInfoPage = lazy(() =>
	import('./DrozdInfoPage').then(({ DrozdInfoPage }) => ({
		default: DrozdInfoPage,
	}))
);
const RezaList = lazy(() =>
	import('./RezaListPage').then(({ RezaListPage }) => ({
		default: RezaListPage,
	}))
);
const RezaDetail = lazy(() =>
	import('./RezaDetailPage').then(({ RezaDetailPage }) => ({
		default: RezaDetailPage,
	}))
);
const RezaArchiveList = lazy(() =>
	import('./RezaArchiveListPage').then(({ RezaArchiveListPage }) => ({
		default: RezaArchiveListPage,
	}))
);
const RezaTemplateList = lazy(() =>
	import('./RezaTemplateListPage').then(({ RezaTemplateListPage }) => ({
		default: RezaTemplateListPage,
	}))
);
const RezaTemplateDetail = lazy(() =>
	import('./RezaTemplateDetailPage').then(({ RezaTemplateDetailPage }) => ({
		default: RezaTemplateDetailPage,
	}))
);

const FormReturnPage = lazy(() =>
	import('./FormReturnPage').then(({ FormReturnPage }) => ({
		default: FormReturnPage,
	}))
);

export const getPageRoutes = (
	extraConfig: ExtraConfig['pageRoutes']
): RouteObject[] => [
	{
		index: true,
		element: (
			<AuthorizedRoute>
				<DashboardPage />
			</AuthorizedRoute>
		),
	},
	{
		path: getPagePartSlug('registr-ZP'),
		element: (
			<AuthorizedRoute>
				<RegistrZPPage />
			</AuthorizedRoute>
		),
	},
	{
		// /autorizace itself is an nginx-owned NIA return endpoint; the SPA page lives one level below
		path: `${getPagePartSlug('autorizace')}/${getPagePartSlug(
			'autorizace.vysledek'
		)}`,
		element: (
			<AuthorizedRoute>
				<AutorizacePage />
			</AuthorizedRoute>
		),
	},
	{
		path: getPagePartSlug('registr-SV'),
		children: [
			{
				index: true,
				element: (
					<AuthorizedRoute>
						<RegistrSVListPage />
					</AuthorizedRoute>
				),
			},
			{
				path: getPagePartSlug('registr-SV.detail'),
				element: (
					<AuthorizedRoute>
						<RegistrSVDetailPage />
					</AuthorizedRoute>
				),
			},
		],
	},
	{
		path: getPagePartSlug('zdravotnicka-dokumentace'),
		element: (
			<AuthorizedRoute>
				<ZdravDokumentacePage />
			</AuthorizedRoute>
		),
	},
	{
		path: getPagePartSlug('podpora'),
		element: <PodporaPage extraConfig={extraConfig.supportConfig} />,
	},
	{
		path: getPagePartSlug('moje-soubory'),
		element: (
			<AuthorizedRoute>
				<MojeSouboryPage />
			</AuthorizedRoute>
		),
	},
	{
		path: getPagePartSlug('uzivatelska-prirucka'),
		element: <UserManualPage />,
	},
	{
		path: getPagePartSlug('drozd'),
		element: <DrozdInfoPage />,
	},
	{
		path: getPagePartSlug('reza'),
		children: [
			{
				index: true,
				element: (
					<AuthorizedRoute>
						<RezaList />
					</AuthorizedRoute>
				),
			},
			{
				path: getPagePartSlug('reza.templates'),
				children: [
					{
						index: true,
						element: (
							<AuthorizedRoute>
								<RezaTemplateList />
							</AuthorizedRoute>
						),
					},
					{
						path: getPagePartSlug('reza.templates.detail'),
						element: (
							<AuthorizedRoute>
								<RezaTemplateDetail />
							</AuthorizedRoute>
						),
					},
				],
			},
			{
				path: getPagePartSlug('reza.detail'),
				element: (
					<AuthorizedRoute>
						<RezaDetail {...extraConfig.rezaDetailConfig} />
					</AuthorizedRoute>
				),
			},
			{
				path: getPagePartSlug('reza.archive'),
				element: (
					<AuthorizedRoute>
						<RezaArchiveList />
					</AuthorizedRoute>
				),
			},
		],
	},
	{
		path: 'formular/navrat',
		element: <FormReturnPage />,
	},
];
